<script setup lang="ts">
// Give Feedback (web): stars, a category, a message (up to 5,000
// characters), an optional screenshot, and a short context the server adds
// to (app, version, OS, this page, the account's state). Never files,
// credentials or logs. Thanks show only after the server confirms.
// Signed out (the pricing page), it's guest feedback with spam checks.
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import Dialog from 'primevue/dialog'
import { useAuthStore } from '@/stores/auth'
import { cloud } from '@/services/cloud'
import { useFeedbackDialog } from '@/composables/useFeedbackDialog'
import StarRating from './StarRating.vue'
import { BubbleChatIcon, Cancel01Icon, Image02Icon, Loading03Icon } from '@/components/icons'
import { version as appVersion } from '../../../package.json'

const auth = useAuthStore()
const route = useRoute()
const { open, kind } = useFeedbackDialog()

const CATEGORIES = [
  { label: 'General Feedback', value: 'general' },
  { label: 'Feature Request', value: 'feature_request' },
  { label: 'Bug Report', value: 'bug_report' },
  { label: 'User Experience', value: 'user_experience' },
  { label: 'Performance', value: 'performance' },
  { label: 'Pricing & Subscription', value: 'pricing_subscription' },
  { label: 'Cloud Synchronization', value: 'cloud_sync' },
  { label: 'Other', value: 'other' },
]
const REASONS = [
  { label: 'Price', value: 'price' },
  { label: 'Missing Features', value: 'missing_features' },
  { label: 'Bugs or Performance', value: 'bugs_performance' },
  { label: 'Difficult to Use', value: 'difficult_to_use' },
  { label: "Didn't Have Enough Time", value: 'not_enough_time' },
  { label: "Didn't Need the Features", value: 'didnt_need' },
  { label: 'Prefer a One-Time Purchase', value: 'prefer_one_time' },
  { label: 'Other', value: 'other' },
]
const MAX = 5000
const MAX_SHOT = 5 * 1024 * 1024

const key = ref(crypto.randomUUID())
const rating = ref<number | null>(null)
const category = ref('general')
const message = ref('')
const reason = ref<string | null>(null)
const guestEmail = ref('')
const website = ref('') // honeypot: people never see or fill it
const shot = ref<{ name: string; data: string } | null>(null)
const startedAt = ref(Date.now())
const busy = ref(false)
const error = ref('')
const done = ref(false)

watch(open, (o) => {
  if (o) {
    if (done.value) reset()
    startedAt.value = Date.now()
    if (kind.value === 'cancellation') category.value = 'pricing_subscription'
  }
})

function reset() {
  key.value = crypto.randomUUID()
  rating.value = null
  category.value = 'general'
  message.value = ''
  reason.value = null
  shot.value = null
  error.value = ''
  done.value = false
}

const signedIn = computed(() => !!auth.user)
const canSend = computed(() => !busy.value && message.value.length <= MAX && (rating.value !== null || message.value.trim().length > 0 || !!reason.value))

function os(): string {
  const ua = navigator.userAgent
  if (/Windows/.test(ua)) return 'Windows'
  if (/Mac OS X/.test(ua)) return /iPhone|iPad/.test(ua) ? 'iOS' : 'macOS'
  if (/Android/.test(ua)) return 'Android'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Other'
}

async function pickShot(e: Event) {
  error.value = ''
  const f = (e.target as HTMLInputElement).files?.[0]
  ;(e.target as HTMLInputElement).value = ''
  if (!f) return
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
    error.value = 'A screenshot must be a PNG, JPEG or WebP image.'
    return
  }
  if (f.size > MAX_SHOT) {
    error.value = 'A screenshot can be at most 5 MB.'
    return
  }
  const data = await new Promise<string>((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''))
    r.onerror = () => reject(new Error('The screenshot couldn’t be read.'))
    r.readAsDataURL(f)
  })
  shot.value = { name: f.name, data }
}

async function send() {
  busy.value = true
  error.value = ''
  try {
    await cloud.sendFeedback({
      client_key: key.value,
      kind: kind.value,
      rating: rating.value,
      category: category.value,
      message: message.value,
      not_subscribing_reason: kind.value === 'cancellation' ? reason.value : null,
      app: 'web',
      app_version: appVersion,
      os: os(),
      page: route.path,
      context: {
        locale: navigator.language.slice(0, 20),
        window: `${window.innerWidth}x${window.innerHeight}`,
        theme: document.documentElement.classList.contains('light') ? 'light' : 'dark',
      },
      screenshot: shot.value?.data ?? null,
      ...(signedIn.value ? {} : { guest_email: guestEmail.value || null, website: website.value, started_at: startedAt.value }),
    })
    if (kind.value === 'cancellation') cloud.setPrompt('cancellation', 'submitted').catch(() => {})
    done.value = true
  } catch (e) {
    error.value = (e as Error).message
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Dialog v-model:visible="open" modal :style="{ width: 'min(540px, calc(100vw - 32px))' }" :show-header="false">
    <div v-if="done" class="pt-6 pb-2 text-center space-y-3" role="status">
      <img src="/favicon.png" alt="" class="size-16 mx-auto" />
      <h2 class="text-lg font-semibold">Thanks for helping improve PrintHQ!</h2>
      <p class="text-fg-2">Your feedback has been sent to the PrintHQ team. Every suggestion helps us build a better workshop experience.</p>
      <button class="btn-primary" @click="open = false">Done</button>
    </div>
    <form v-else class="pt-5 pb-1 space-y-4" @submit.prevent="send">
      <div class="flex items-start gap-3">
        <div class="size-10 rounded-xl bg-accent-soft text-accent-text flex items-center justify-center shrink-0"><BubbleChatIcon :size="20" /></div>
        <div class="flex-1">
          <h2 class="text-base font-semibold">{{ kind === 'cancellation' ? 'Before you go' : 'Give Feedback' }}</h2>
          <p class="text-fg-2 text-xs mt-0.5">{{ kind === 'cancellation' ? 'Optional: it helps us make PrintHQ better.' : 'It goes straight to the PrintHQ team.' }}</p>
        </div>
        <button type="button" class="icon-btn-sm" aria-label="Close" @click="open = false"><Cancel01Icon :size="14" /></button>
      </div>
      <div>
        <span class="label">How would you rate PrintHQ?</span>
        <StarRating v-model="rating" label="How would you rate PrintHQ?" />
      </div>
      <div v-if="kind === 'cancellation'">
        <span class="label">What's the main reason you're canceling? (optional)</span>
        <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Main reason">
          <button v-for="r in REASONS" :key="r.value" type="button" role="radio" :aria-checked="reason === r.value"
            class="px-2.5 py-1 rounded-full border text-xs" :class="reason === r.value ? 'border-accent bg-accent-soft text-accent-text' : 'border-line text-fg-2 hover:bg-well'"
            @click="reason = reason === r.value ? null : r.value">{{ r.label }}</button>
        </div>
      </div>
      <div v-else>
        <label class="label" for="fb-category">Category</label>
        <select id="fb-category" v-model="category" class="input">
          <option v-for="c in CATEGORIES" :key="c.value" :value="c.value">{{ c.label }}</option>
        </select>
      </div>
      <div>
        <label class="label" for="fb-message">Message</label>
        <textarea id="fb-message" v-model="message" class="input !h-auto py-2 min-h-[120px]" :maxlength="MAX" placeholder="Tell us about your experience with PrintHQ." />
        <div class="flex justify-end text-[11px]" :class="message.length > MAX - 200 ? 'text-warn-text' : 'text-fg-3'">{{ message.length.toLocaleString() }} / {{ MAX.toLocaleString() }}</div>
      </div>
      <div class="flex items-center gap-2">
        <label class="btn-subtle btn-sm cursor-pointer">
          <Image02Icon :size="13" />{{ shot ? 'Change Screenshot' : 'Add a Screenshot (optional)' }}
          <input type="file" accept="image/png,image/jpeg,image/webp" class="sr-only" @change="pickShot" />
        </label>
        <span v-if="shot" class="text-[11px] text-fg-2 truncate flex-1" :title="shot.name">{{ shot.name }}</span>
        <button v-if="shot" type="button" class="btn-ghost btn-sm" @click="shot = null">Remove</button>
      </div>
      <div v-if="!signedIn">
        <label class="label" for="fb-email">Your email (optional, for a reply)</label>
        <input id="fb-email" v-model="guestEmail" class="input" type="email" autocomplete="email" />
        <div class="sr-only" aria-hidden="true"><label for="fb-website">Website</label><input id="fb-website" v-model="website" tabindex="-1" autocomplete="off" /></div>
      </div>
      <p class="text-[11px] text-fg-3">Sent with {{ signedIn ? 'your account, ' : '' }}the app version, your OS and this page. Never your files, passwords or logs.</p>
      <p v-if="error" class="text-danger-text text-xs" role="alert">{{ error }}</p>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost" @click="open = false">{{ kind === 'cancellation' ? 'Skip' : 'Cancel' }}</button>
        <button class="btn-primary" type="submit" :disabled="!canSend"><Loading03Icon v-if="busy" :size="14" class="animate-spin" />{{ busy ? 'Sending…' : 'Send Feedback' }}</button>
      </div>
    </form>
  </Dialog>
</template>
